import { getUserFriendlyError } from "@common/errorHandler";
import { BaseUrl } from "@config/api";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

interface FetchUsersParams {
  page: number;
  pageSize: number;
  isActive?: boolean;
  hospitalId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  securityGroupId?: number;
}

// Define the async thunk to fetch users
export const fetchUsers = createAsyncThunk(
  "userList/fetchUsers",
  async (params: FetchUsersParams, { rejectWithValue }) => {
    try {
      const {
        page,
        pageSize,
        isActive,
        hospitalId,
        search,
        sortBy,
        sortOrder,
        securityGroupId,
      } = params;
      let url = `${BaseUrl}/api/user/${hospitalId}/csl?Page=${page}&PageSize=${pageSize}`;

      if (search) {
        const encodedeSearchString = encodeURIComponent(search);
        url += `&Search=${encodedeSearchString}`;
      }
      if (isActive) url += `&IsActive=${isActive === "Active"}`;
      if (securityGroupId) url += `&SecurityGroupId=${securityGroupId}`;
      if (sortBy && sortOrder)
        url += `&SortBy=${sortBy}&SortOrder=${sortOrder}`;
      const response = await axios.get(url);

      if (response.data) {
        return response.data.data[0];
      } else {
        return rejectWithValue(
          response.data.message || "Failed to fetch users"
        );
      }
    } catch (error: any) {
      return rejectWithValue(getUserFriendlyError(error));
    }
  }
);

interface User {
  userId: number;
  hospitalId?: string;
  zendeskSupportGroupId: number;
  zendeskUserId: number;
  securityGroupId: number;
  firstName: string;
  lastName: string;
  emailID: string;
  isOnCallStatus: boolean;
  userTypeId: number;
  isActive: boolean;
  createdBy: number;
  createdDate: string;
  updatedBy?: number;
  updatedDate?: string;
  countryCode?: string;
  contactNumber?: string;
  deactivationReson?: string;
}

interface Organization {
  id: number;
  hospitalId: string;
  hospitalName: string;
  zendeskOrgId: number;
}

interface SecurityGroup {
  id: number;
  groupName: string;
}

interface UserListState {
  users: User[];
  organizations: Organization[];
  securityGroups: SecurityGroup[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  organizationsLoading: boolean; // Add loading state for organizations
  organizationsError: string | null; // Add error state for organizations
}

const initialState: UserListState = {
  users: [],
  organizations: [],
  securityGroups: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  totalPages: 1,
  loading: false,
  error: null,
  organizationsLoading: false, // Initialize loading state for organizations
  organizationsError: null, // Initialize error state for organizations
};

const userListSlice = createSlice({
  name: "userList",
  initialState,
  reducers: {
    clearUsers: (state) => {
      state.users = [];
      state.organizations = [];
      state.securityGroups = [];
      state.totalCount = 0;
      state.currentPage = 1;
      state.pageSize = 20;
      state.totalPages = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.organizations = action.payload.organizationDetails;
        state.securityGroups = action.payload.securityGroups;
        state.totalCount = action.payload.totalCount;
        state.pageSize = action.payload.pageSize;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.users = [];
        state.organizations = [];
        state.securityGroups = [];
        state.totalCount = 0;
        state.currentPage = 1;
        state.pageSize = 20;
        state.totalPages = 1;
      });
  },
});

export const { clearUsers, setPage, setPageSize } = userListSlice.actions;
export default userListSlice.reducer;

export const selectUsers = (state: { userList: UserListState }) =>
  state.userList.users;
export const selectOrganizations = (state: { userList: UserListState }) =>
  state.userList.organizations;
export const selectSecurityGroups = (state: { userList: UserListState }) =>
  state.userList.securityGroups;
export const selectTotalCount = (state: { userList: UserListState }) =>
  state.userList.totalCount;
export const selectCurrentPage = (state: { userList: UserListState }) =>
  state.userList.currentPage;
export const selectPageSize = (state: { userList: UserListState }) =>
  state.userList.pageSize;
export const selectTotalPages = (state: { userList: UserListState }) =>
  state.userList.totalPages;
export const selectUserListLoading = (state: { userList: UserListState }) =>
  state.userList.loading;
export const selectUserListError = (state: { userList: UserListState }) =>
  state.userList.error;

// Selectors for organizations
export const selectOrganizationsLoading = (state: {
  userList: UserListState;
}) => state.userList.organizationsLoading;
export const selectOrganizationsError = (state: { userList: UserListState }) =>
  state.userList.organizationsError;
